import { getErrorMessage } from '@xver/utils/error';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
    IconCheck,
    IconExclamationCircle,
    IconEye,
    IconPlus,
    IconTrash,
} from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type { DomainMappingResponse } from '@xver/apis/v1/kufer/domain-mappings/model';
import { Button, ConfirmActionModal } from '@xver/components/Atoms';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { DataTable, DataTableColumn } from '@xver/components/Organisms';
import { formatDate } from '@xver/utils/date';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DOMAIN_MAPPINGS_QUERY_KEY, useDomainMappingsQuery } from '../hooks/useDomainMappingsQuery';
import AddDomainModal from './AddDomainModal';
import ViewDomainRecordsModal from './ViewDomainRecordsModal';

interface DomainsTabPanelProps {
    containerId: string;
    /** False with view-only access: domains are listed without add or remove. */
    canManage: boolean;
}

const DNS_MODE_STYLES: Record<string, { color: string; backgroundColor: string }> = {
    direct: { color: 'var(--color-status-teal)', backgroundColor: 'rgba(45, 212, 191, 0.15)' },
    proxied: { color: 'var(--color-status-info)', backgroundColor: 'rgba(96, 165, 250, 0.15)' },
    unpointed: {
        color: 'var(--color-status-warning)',
        backgroundColor: 'rgba(251, 191, 36, 0.15)',
    },
};

export default function DomainsTabPanel({ containerId, canManage }: DomainsTabPanelProps) {
    const { t, i18n } = useTranslation('containerEngineInstance');
    const queryClient = useQueryClient();
    const [addOpened, setAddOpened] = useState(false);
    const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);
    const [pendingDelete, setPendingDelete] = useState<DomainMappingResponse | null>(null);
    const [viewMapping, setViewMapping] = useState<DomainMappingResponse | null>(null);

    const { data, isPending } = useDomainMappingsQuery(containerId);
    const mappings = data?.domain_mappings ?? [];

    const deleteMutation = useMutation({
        mutationFn: (mappingId: string) =>
            Endpoints.v1.Kufer.DomainMappings.deleteDomainMapping(containerId, mappingId),
        onSuccess: async () => {
            notifications.show({
                title: t('domains.deleteSuccessTitle', 'Domain removed'),
                message: t('domains.deleteSuccessMessage', 'The domain mapping has been deleted.'),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            await queryClient.invalidateQueries({
                queryKey: [DOMAIN_MAPPINGS_QUERY_KEY, containerId],
            });
        },
        onError: (err) => {
            notifications.show({
                title: t('domains.deleteFailedTitle', 'Failed to remove domain'),
                message: getErrorMessage(err),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    const handleDelete = (m: DomainMappingResponse) => {
        setPendingDelete(m);
        openDelete();
    };

    const confirmDelete = () => {
        if (!pendingDelete) return;
        deleteMutation.mutate(pendingDelete.id, {
            onSuccess: () => {
                closeDelete();
                setPendingDelete(null);
            },
        });
    };

    const dnsModeLabels: Record<string, string> = {
        direct: t('domains.dnsModeDirect', 'Direct'),
        proxied: t('domains.dnsModeProxied', 'Proxied (Cloudflare)'),
        unpointed: t('domains.dnsModeUnpointed', 'Not pointed'),
    };

    const columns: DataTableColumn<DomainMappingResponse>[] = [
        {
            key: 'domain',
            label: t('domains.colDomain', 'Domain'),
            render: (m) => <span className="font-medium text-fg text-sm">{m.domain_name}</span>,
        },
        {
            key: 'dns',
            label: t('domains.colDns', 'DNS setup'),
            render: (m) => (
                <button
                    type="button"
                    className="p-1 rounded hover:bg-fg/8 transition-colors text-fg/70 hover:text-fg"
                    aria-label="View DNS setup"
                    title={t('domains.colDns', 'DNS setup')}
                    onClick={(e) => {
                        e.stopPropagation();
                        setViewMapping(m);
                    }}
                >
                    <IconEye size={16} stroke={1.5} />
                </button>
            ),
        },
        {
            key: 'status',
            label: t('domains.colStatus', 'Status'),
            render: (m) => (
                <div className="flex flex-col items-start gap-1">
                    {m.verified_at ? (
                        <span
                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium"
                            style={{
                                color: 'var(--color-status-success)',
                                backgroundColor: 'rgba(34, 197, 94, 0.15)',
                            }}
                        >
                            <IconCheck size={12} />
                            {t('domains.statusVerified', 'Verified')}
                        </span>
                    ) : (
                        <span
                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium"
                            style={{
                                color: 'var(--color-status-warning)',
                                backgroundColor: 'rgba(251, 191, 36, 0.15)',
                            }}
                        >
                            <span
                                className="w-1.5 h-1.5 rounded-full"
                                style={{ backgroundColor: '#fbbf24' }}
                            />
                            <span className="pb-0.5">{t('domains.statusPending', 'Pending')}</span>
                        </span>
                    )}
                    {m.dns_mode && DNS_MODE_STYLES[m.dns_mode] && (
                        <span
                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium"
                            style={DNS_MODE_STYLES[m.dns_mode]}
                        >
                            {dnsModeLabels[m.dns_mode] ?? m.dns_mode}
                        </span>
                    )}
                </div>
            ),
        },
        {
            key: 'created',
            label: t('domains.colCreated', 'Created'),
            render: (m) => (
                <span className="text-xs text-fg/68">
                    {formatDate(m.created_at, i18n.language, t)}
                </span>
            ),
        },
    ];

    return (
        <>
            <DialogBox
                label={t('domains.title', 'Custom domains')}
                description={t('domains.tabDescription', 'Map your own domains to this container.')}
                rightDescription={
                    canManage && (
                        <Button
                            size="sm"
                            variant="secondary"
                            leftIcon={<IconPlus size={14} />}
                            onClick={() => setAddOpened(true)}
                        >
                            {t('domains.add', 'Add domain')}
                        </Button>
                    )
                }
            >
                <DataTable
                    caption={t('domains.title', 'Custom domains')}
                    columns={columns}
                    rows={mappings}
                    isLoading={isPending}
                    rowKey={(m) => m.id}
                    emptyMessage={t('domains.empty', 'No custom domains yet.')}
                    actions={
                        canManage
                            ? (m) => (
                                  <div className="flex justify-end">
                                      <button
                                          type="button"
                                          className="p-1 rounded hover:bg-fg/8 transition-colors text-red-400 light:text-red-700 disabled:opacity-50"
                                          aria-label={t(
                                              'domains.deleteConfirmLabel',
                                              'Remove domain'
                                          )}
                                          disabled={deleteMutation.isPending}
                                          onClick={() => handleDelete(m)}
                                      >
                                          <IconTrash size={16} stroke={1.5} />
                                      </button>
                                  </div>
                              )
                            : undefined
                    }
                />
            </DialogBox>
            <AddDomainModal
                containerId={containerId}
                opened={addOpened}
                onClose={() => setAddOpened(false)}
            />

            <ViewDomainRecordsModal
                mapping={viewMapping}
                opened={viewMapping !== null}
                onClose={() => setViewMapping(null)}
            />

            <ConfirmActionModal
                opened={deleteOpened}
                onClose={() => {
                    if (deleteMutation.isPending) return;
                    closeDelete();
                    setPendingDelete(null);
                }}
                title={t('domains.deleteConfirmTitle', 'Remove domain mapping?')}
                message={t('domains.deleteConfirmBody', {
                    domainName: pendingDelete?.domain_name ?? '',
                    defaultValue: '"{{domainName}}" will be removed from this container.',
                })}
                confirmLabel={t('domains.deleteConfirmLabel', 'Remove domain')}
                confirmVariant="danger"
                isLoading={deleteMutation.isPending}
                onConfirm={confirmDelete}
            />
        </>
    );
}
